// Write helpers for the Teams / Timesheets / Settings pages.
// (Candidates to move into lib/actions.ts; kept here to avoid touching shared files.)
import type { Op } from '@shared/sync';
import type { TaskType, User, Workspace, WorkspaceRole } from '@shared/types';
import { newId } from '@shared/ids';
import { PALETTE } from '@shared/constants';
import { del, getDb, mutate, patch, put } from '@/lib/store';
import { compressSquareImage } from '@/lib/image';
import { postUpload } from '@/lib/upload';

export function updateWorkspace(id: string, changes: Partial<Workspace>) {
  mutate([patch('workspaces', id, changes)]);
}

/** Compress a picked photo (256 px square, WebP) and make it the workspace logo. Resolves to the uploaded size. */
export async function uploadWorkspaceLogo(id: string, file: File): Promise<number> {
  const small = await compressSquareImage(file, 256);
  const { url } = await postUpload(small, id);
  updateWorkspace(id, { logoUrl: url });
  return small.size;
}

export function updateUser(id: string, changes: Partial<User>) {
  mutate([patch('users', id, changes)]);
}

export function removeMember(workspaceId: string, userId: string) {
  const w = getDb().workspaces[workspaceId];
  if (!w) return;
  const roles = { ...w.roles };
  delete roles[userId];
  mutate([patch('workspaces', workspaceId, { memberIds: w.memberIds.filter((id) => id !== userId), roles })]);
}

/** Make someone an admin, a member or a guest (the owner and admins can). */
export function setRole(workspaceId: string, userId: string, role: WorkspaceRole | 'member') {
  const w = getDb().workspaces[workspaceId];
  if (!w || w.ownerId === userId) return;
  const roles = { ...w.roles };
  if (role === 'member') delete roles[userId];
  else roles[userId] = role;
  mutate([patch('workspaces', workspaceId, { roles })]);
}

/** Hand the workspace to another member or admin; the current owner stays on as an admin. */
export function transferOwnership(workspaceId: string, userId: string) {
  const w = getDb().workspaces[workspaceId];
  if (!w || w.ownerId === userId) return;
  const roles: Record<string, WorkspaceRole> = { ...w.roles, [w.ownerId]: 'admin' };
  delete roles[userId];
  mutate([patch('workspaces', workspaceId, { ownerId: userId, roles })]);
}

export function taskTypesOf(workspaceId: string): TaskType[] {
  return Object.values(getDb().taskTypes)
    .filter((t) => t.workspaceId === workspaceId)
    .sort((a, b) => a.orderIndex - b.orderIndex);
}

export function createTaskType(workspaceId: string): TaskType {
  const existing = taskTypesOf(workspaceId);
  const type: TaskType = {
    id: newId(),
    workspaceId,
    name: 'New type',
    namePlural: 'New types',
    icon: 'Star',
    color: PALETTE[(existing.length * 3 + 1) % PALETTE.length],
    orderIndex: (existing[existing.length - 1]?.orderIndex ?? 0) + 1000,
    createdAt: Date.now(),
  };
  mutate([put('taskTypes', type)]);
  return type;
}

export function updateTaskType(id: string, changes: Partial<TaskType>) {
  mutate([patch('taskTypes', id, changes)]);
}

/** Deletes a custom task type; tasks (and list defaults) using it fall back to the default type. */
export function deleteTaskType(id: string) {
  const db = getDb();
  const type = db.taskTypes[id];
  if (!type || type.builtin) return;
  const ops: Op[] = [del('taskTypes', id)];
  for (const t of Object.values(db.tasks)) if (t.taskTypeId === id) ops.push(patch('tasks', t.id, { taskTypeId: null }));
  for (const l of Object.values(db.lists)) if (l.defaultTaskTypeId === id) ops.push(patch('lists', l.id, { defaultTaskTypeId: null }));
  mutate(ops);
}

export function taskTypeUsage(id: string): number {
  return Object.values(getDb().tasks).filter((t) => t.taskTypeId === id).length;
}

export function deleteTimeEntry(id: string) {
  mutate([del('timeEntries', id)]);
}
